import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Users, UserPlus, Crown, Shield, User, Trash2, ChevronDown, LogOut } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Logo from './Logo';
import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const roleBadge = {
  owner: { label: 'Owner', icon: Crown, cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  admin: { label: 'Admin', icon: Shield, cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  member: { label: 'Member', icon: User, cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' }
};

const TeamManagement = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => { fetchTeam(); }, [token]);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/teams/my-team`, { headers: { Authorization: `Bearer ${token}` } });
      setTeamData(res.data);
    } catch { setTeamData({ has_team: false }); }
    finally { setLoading(false); }
  };

  const createTeam = async () => {
    if (!teamName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/teams/create`, { name: teamName }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Team created!' });
      fetchTeam();
      setTeamName('');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to create team', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API}/teams/invite`, { email: inviteEmail, role: inviteRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: res.data.message });
      setInviteEmail('');
      setShowInvite(false);
      fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to invite', variant: 'destructive' });
    } finally { setInviting(false); }
  };

  const removeMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      await axios.delete(`${API}/teams/members/${memberId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `${email} removed` });
      fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to remove', variant: 'destructive' });
    }
  };

  const changeRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/teams/members/${memberId}/role`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `Role updated to ${newRole}` });
      fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to update role', variant: 'destructive' });
    }
  };

  const leaveTeam = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    try {
      await axios.delete(`${API}/teams/leave`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'You left the team' });
      fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to leave', variant: 'destructive' });
    }
  };

  const deleteTeam = async () => {
    if (!window.confirm('Delete this team? This will remove all members. This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/teams/delete`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Team deleted' });
      fetchTeam();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
      <p className="text-gray-400">Loading...</p>
    </div>
  );

  const myRole = teamData?.my_role;
  const isOwnerOrAdmin = myRole === 'owner' || myRole === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="team-management-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Logo linkTo="/dashboard" />
          <div className="h-6 w-px bg-white/10" />
          <h1 className="text-xl font-bold text-white">Team Management</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {!teamData?.has_team ? (
          /* No team — create one */
          <Card className="bg-gray-900/50 border-white/10">
            <CardContent className="py-12 text-center space-y-6">
              <Users className="w-12 h-12 text-gray-600 mx-auto" />
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Create Your Team</h3>
                <p className="text-gray-400 text-sm mb-6">Invite team members to manage your AI receptionist together.</p>
              </div>
              <div className="max-w-sm mx-auto space-y-3">
                <Input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Team name (e.g. Acme Corp)"
                  className="bg-black/30 border-white/10 text-white"
                  data-testid="team-name-input"
                />
                <Button onClick={createTeam} disabled={creating || !teamName.trim()} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-team-btn">
                  {creating ? 'Creating...' : 'Create Team'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Team header */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2" data-testid="team-name">
                    <Users className="w-5 h-5 text-purple-400" />
                    {teamData.team.name}
                  </CardTitle>
                  <p className="text-gray-500 text-sm mt-1">
                    {teamData.members.length} member{teamData.members.length !== 1 ? 's' : ''} — You are {myRole}
                  </p>
                </div>
                {isOwnerOrAdmin && (
                  <Button onClick={() => setShowInvite(!showInvite)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="invite-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Invite
                  </Button>
                )}
              </CardHeader>

              {/* Invite form */}
              {showInvite && (
                <CardContent className="pt-0 border-t border-white/5">
                  <div className="pt-4 space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Label className="text-gray-400 text-xs">Email</Label>
                        <Input
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="colleague@company.com"
                          className="bg-black/30 border-white/10 text-white mt-1"
                          data-testid="invite-email-input"
                        />
                      </div>
                      <div className="w-32">
                        <Label className="text-gray-400 text-xs">Role</Label>
                        <select
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="w-full mt-1 bg-black/30 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                          data-testid="invite-role-select"
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <Button onClick={inviteMember} disabled={inviting || !inviteEmail.trim()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="send-invite-btn">
                      {inviting ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Members list */}
            <div className="space-y-2" data-testid="members-list">
              {teamData.members.map((m) => {
                const badge = roleBadge[m.role] || roleBadge.member;
                const BadgeIcon = badge.icon;
                return (
                  <Card key={m.id} className="bg-gray-900/50 border-white/10" data-testid={`member-${m.email}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                          <BadgeIcon className={`w-5 h-5 ${m.role === 'owner' ? 'text-amber-400' : m.role === 'admin' ? 'text-purple-400' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium text-sm">{m.full_name || m.email}</p>
                            {m.status === 'pending' && (
                              <span className="text-xs bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 px-2 py-0.5 rounded-full">Pending</span>
                            )}
                          </div>
                          <p className="text-gray-500 text-xs">{m.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${badge.cls}`}>
                          {badge.label}
                        </span>
                        {/* Role change / remove actions */}
                        {myRole === 'owner' && m.role !== 'owner' && (
                          <div className="flex items-center gap-1 ml-2">
                            <select
                              value={m.role}
                              onChange={(e) => changeRole(m.id, e.target.value)}
                              className="bg-black/30 border border-white/10 text-gray-300 text-xs rounded px-2 py-1"
                              data-testid={`role-select-${m.email}`}
                            >
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                            </select>
                            <Button
                              variant="ghost"
                              onClick={() => removeMember(m.id, m.email)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                              data-testid={`remove-${m.email}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Team actions */}
            <div className="flex justify-end gap-3 pt-4">
              {myRole !== 'owner' && (
                <Button variant="outline" onClick={leaveTeam} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="leave-team-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Leave Team
                </Button>
              )}
              {myRole === 'owner' && (
                <Button variant="outline" onClick={deleteTeam} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-team-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Team
                </Button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default TeamManagement;
